
import { ArrowLeft, MoreHorizontal, Wifi, Battery, Signal, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { usePersonalConnections } from "@/hooks/usePersonalConnections";

interface GprofileHeaderProps {
  displayName: string;
  onBack: () => void;
  userId: string;
}

const GprofileHeader = ({ displayName, onBack, userId }: GprofileHeaderProps) => {
  const { user } = useAuth();
  const { isPersonallyKnown, loading, togglePersonalConnection } = usePersonalConnections(userId);

  const handleReport = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_reports')
        .insert({
          reporter_id: user.id,
          reported_id: userId,
          reason: 'inappropriate_content',
          description: 'Reported from profile view'
        });

      if (error) throw error;

      toast({
        title: "Report submitted",
        description: "Thank you for helping keep our community safe.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit report",
        variant: "destructive",
      });
    }
  };

  const handleBlock = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_blocks')
        .insert({
          blocker_id: user.id,
          blocked_id: userId,
          reason: 'user_preference'
        });

      if (error) throw error;

      toast({
        title: "User blocked",
        description: "You won't see this user in your discovery feed.",
      });
      onBack();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to block user",
        variant: "destructive",
      });
    }
  };

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: false 
    });
  };

  return (
    <div className="fixed left-0 right-0 z-40 bg-black/90 backdrop-blur-sm" style={{ top: 'calc(100vh / 7)' }}>
      {/* Status Bar */}
      <div className="flex items-center justify-between px-4 py-1 text-xs text-white/70">
        <div className="flex items-center gap-1">
          <Signal className="w-3 h-3" />
          <Wifi className="w-3 h-3" />
        </div>
        <div className="font-medium">
          {getCurrentTime()}
        </div>
        <div className="flex items-center gap-1">
          <span>100%</span>
          <Battery className="w-3 h-3" />
        </div>
      </div>

      {/* Navigation Bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-400/10"
        >
          <ArrowLeft className="w-6 h-6" />
        </Button>

        <h1 className="text-lg font-semibold text-white truncate max-w-48">
          {displayName}
        </h1>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <MoreHorizontal className="w-6 h-6" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="end" 
            className="bg-gray-900 border-gray-700 text-white"
          >
            <DropdownMenuItem 
              onClick={togglePersonalConnection}
              disabled={loading}
              className="hover:bg-gray-800 text-white"
            >
              <UserCheck className="w-4 h-4 mr-2" />
              {isPersonallyKnown ? "Remove from Personally Known" : "Mark as Personally Known"}
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={handleReport}
              className="hover:bg-gray-800 text-red-400"
            >
              Report User
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={handleBlock}
              className="hover:bg-gray-800 text-red-400"
            >
              Block User
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default GprofileHeader;
